import { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  useTheme,
  Typography,
  useMediaQuery,
  Box,
  LinearProgress,
  FormControlLabel,
  Checkbox,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  alpha,
  Tooltip,
  IconButton
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import Avatar from "@mui/material/Avatar";
import { InsertDriveFileOutlined, UploadFileOutlined } from '@mui/icons-material';

import { useApplicationContext } from '../../context/application';
import { useAuthContext } from '../../context/auth';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import useDialogStyles from '../../hooks/useDialogStyles';
import { formatFileSize, formatFileDate } from '../../utils/utils';

const validator = require('validator');

const Edit = ({ application, appTypes } ) => {
  const theme = useTheme();

  const axiosPrivate = useAxiosPrivate();
  const { dispatch } = useApplicationContext();
  const { auth } = useAuthContext();
  const ds = useDialogStyles();
  const isDark = theme.palette.mode === 'dark';
  const accent = theme.palette.secondary.main;

  const closeDialog = () => { setShow(false); setError(null); };

  const [error, setError] = useState(null);
  const [show, setShow] = useState(false);
  const [selectedAppType, setSelectedAppType] = useState(application.appType || '');
  const [selectedFileName, setSelectedFileName] = useState(application.originalFileName || '');
  const [selectedFileSize, setSelectedFileSize] = useState(application.fileSize ? formatFileSize(application.fileSize) : 'N/A');
  const [selectedFileCreatedAt, setSelectedFileCreatedAt] = useState(
    application.fileCreatedAt || null
  );
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [iconPreview, setIconPreview] = useState(null);

  const versionRef = useRef('');
  const fileInputRef = useRef(null);
  const activeRef = useRef(application.active || false);

  useEffect(() => {
    if (application?.iconPath) {
        setIconPreview(
            `${process.env.REACT_APP_SERVER_URL}${application.iconPath}?t=${Date.now()}`
        );
    } else {
        setIconPreview(null);
    }
  }, [application]);

  const handleEdit = async () => {
    const updateApplication = {
      appType: selectedAppType,
      version: versionRef.current.value,
      originalFileName: selectedFileName,
      file: selectedFile,
      fileCreatedAt: selectedFileCreatedAt,
      active: activeRef.current.checked,
    };

    const prevApplication = [application.appType, application.version, application.originalFileName, application.fileCreatedAt, application.active];

    Object.keys(updateApplication).forEach((key) => {
      if (
        validator.isEmpty(updateApplication[key]?.toString() || '', { ignore_whitespace: true }) ||
        prevApplication.includes(updateApplication[key])
      ) {
        delete updateApplication[key];
      }
    });
    
    if (!auth) {
      setError('You must be logged in');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setError(null);

    const formData = new FormData();

    formData.append("id", application._id);
    formData.append("appType", selectedAppType);
    formData.append("version", versionRef.current.value);
    // File is optional: only send it (and its date) when a new one was chosen
    if (selectedFile) {
      formData.append("file", selectedFile);
      if (selectedFileCreatedAt) {
        formData.append("fileCreatedAt", selectedFileCreatedAt);
      }
    }
    formData.append("active", activeRef.current.checked);

    try {
      const response = await axiosPrivate.patch(
        `/api/applications/${application._id}`, formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadProgress(percent);
            }
          },
        }
      );

      dispatch({ type: 'UPDATE_APPLICATION', payload: response.data });
      setError(null);
      setShow(false);
    } catch (error) {
      setError(error.response?.data.error || 'Upload failed');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleOpenFileDialog = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelection = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSelectedFileName(file.name);
    setSelectedFileSize(formatFileSize(file.size));
    setSelectedFileCreatedAt(new Date(file.lastModified).toISOString());
    setSelectedFile(file);
    event.target.value = '';
  };

  return (
    <>
      <Tooltip title="Edit Application">
        <IconButton
          color="secondary"
          size="small"
          style={{ marginRight: 8 }}
          onClick={() => setShow(true)}
        >
          <EditIcon />
        </IconButton>
      </Tooltip>    

      <Dialog
        open={show}
        onClose={isUploading ? undefined : closeDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: ds.paperSx }}
      >
        <DialogTitle sx={ds.titleSx}>Edit application</DialogTitle>

        <DialogContent sx={ds.contentSx}>
          {/* Type + Version + Active */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 140px auto' },
              gap: 2.5,
              alignItems: 'center',
            }}
          >
            <FormControl fullWidth sx={ds.fieldSx}>
              <InputLabel>Application type</InputLabel>
              <Select
                label="Application type"
                value={selectedAppType}
                onChange={(e) => setSelectedAppType(e.target.value)}
                MenuProps={ds.menuProps}
              >
                {appTypes?.map((appType) => (
                  <MenuItem key={appType._id} value={appType._id}>
                    {appType.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Version"
              inputRef={versionRef}
              variant="outlined"
              autoComplete="off"
              defaultValue={application.version}
              sx={ds.fieldSx}
            />

            <FormControlLabel
              control={
                <Checkbox
                  inputRef={activeRef}
                  defaultChecked={application.active || false}
                  sx={{ color: theme.palette.text.secondary, '&.Mui-checked': { color: accent } }}
                />
              }
              label="Active"
              sx={{ color: theme.palette.text.primary, ml: 0.5, mr: 0 }}
            />
          </Box>

          {/* File */}
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileSelection}
          />

          <Box
            sx={{
              mt: 2.5,
              p: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              borderRadius: 2,
              border: `1px dashed ${selectedFileName ? alpha(accent, 0.6) : theme.palette.divider}`,
              backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : alpha(accent, 0.05),
            }}
          >
            {iconPreview ? (
              <Avatar src={iconPreview} alt="" variant="rounded" sx={{ width: 44, height: 44 }} />
            ) : (
              <Avatar
                variant="rounded"
                sx={{ width: 44, height: 44, color: accent, backgroundColor: alpha(accent, 0.12) }}
              >
                <InsertDriveFileOutlined />
              </Avatar>
            )}

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body1"
                noWrap
                sx={{ fontWeight: 600, color: selectedFileName ? 'text.primary' : 'text.secondary' }}
              >
                {selectedFileName || 'No file selected'}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" noWrap>
                Size: {selectedFileSize}
                {'  ·  '}
                Created: {selectedFileCreatedAt ? formatFileDate(selectedFileCreatedAt) : 'N/A'}
              </Typography>
            </Box>

            <Button
              variant="outlined"
              startIcon={<UploadFileOutlined />}
              onClick={handleOpenFileDialog}
              disabled={isUploading}
              sx={{
                flexShrink: 0,
                color: accent,
                borderColor: alpha(accent, 0.6),
                fontWeight: 700,
                '&:hover': { borderColor: accent, backgroundColor: alpha(accent, 0.12) },
              }}
            >
              {selectedFileName ? 'Change' : 'Choose file'}
            </Button>
          </Box>

          {isUploading && (
            <Box sx={{ mt: 2.5 }}>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
                Uploading file... {uploadProgress}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={uploadProgress}
                sx={{
                  height: 8,
                  borderRadius: 5,
                  backgroundColor: alpha(accent, 0.15),
                  '& .MuiLinearProgress-bar': { backgroundColor: accent },
                }}
              />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={ds.actionsSx}>
          <Button onClick={closeDialog} disabled={isUploading} sx={ds.cancelButtonSx}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleEdit} disabled={isUploading} sx={ds.goldButtonSx}>
            {isUploading ? 'Uploading...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Edit;
